import bannerUpdate from "Api/banners/update";
import bannerUpdateImage from "Api/banners/updateImage";
import ButtonPrimary from "Components/Buttons/Primary";
import Form from "Components/Form";
import CheckBox from "Components/Form/Inputs/CheckBox";
import Select from "Components/Form/Inputs/Select";
import TextInput from "Components/Form/Inputs/Text";
import Loading from "Components/Loading";
import FilePondPluginFileValidateSize from "filepond-plugin-file-validate-size";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import useBanner from "Hooks/Banners/useBanner";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { useHistory } from "react-router-dom";
import localeActions from "Redux/Locale/actions";
import { IBannerRequest } from "Services/Banners/banner";
import { bannerPositions } from "..";
import * as Styled from "./styles";

type TParams = {
  bannerId: string;
};

const simpleDateFormat = "DD/MM/YYYY hh:mm";

registerPlugin(FilePondPluginImageExifOrientation);
registerPlugin(FilePondPluginFileValidateSize);

const AdminBannersEdit: React.FC = () => {
  const history = useHistory();

  const { bannerId } = useParams<TParams>();

  const [title, setTitle] = useState<string>();
  const [description, setDescription] = useState<string>();
  const [campaignCode, setCampaignCode] = useState<string>();
  const [link, setLink] = useState<string>();
  const [position, setPosition] = useState<string>();
  const [startAtDate, setStartAtDate] = useState<Date>(new Date());
  const [expiresAtDate, setExpiresAtDate] = useState<Date>(new Date());
  const [enabled, setEnabled] = useState<boolean>(true);
  const [image, setImage] = useState<string>();
  const [file, setFile] = useState<any>([]);

  const dispatch = useDispatch();

  const { data: banner, isLoading, error } = useBanner(bannerId);

  const onFormSubmit = async (bannerRequest: IBannerRequest) => {
    const startAtDateUpdated = moment(
      bannerRequest.start_at,
      simpleDateFormat
    ).utc(true);
    const expiresAtDateUpdated = moment(
      bannerRequest.expires_at,
      simpleDateFormat
    ).utc(true);

    if (
      !bannerRequest.title ||
      !bannerRequest.description ||
      !bannerRequest.campaign_code ||
      !bannerRequest.link ||
      bannerRequest.start_at.indexOf("_") !== -1 ||
      !startAtDateUpdated ||
      bannerRequest.expires_at.indexOf("_") !== -1 ||
      !expiresAtDateUpdated
    ) {
      dispatch(
        localeActions.addTooltip({
          type: "error",
          message: "Por favor, preencha todos os campos obrigatórios.",
        })
      );
      return;
    }

    bannerRequest.start_at = startAtDateUpdated.toISOString();
    bannerRequest.expires_at = expiresAtDateUpdated.toISOString();

    try {
      await bannerUpdate(bannerId, bannerRequest);

      if (file[0]?.file) {
        const formData = new FormData();

        if (file.length > 0) {
          formData.append("image", file[0].file);
        }

        await bannerUpdateImage(bannerId, formData);
      }

      dispatch(
        localeActions.addTooltip({
          type: "success",
          message: "Banner atualizado com sucesso!",
        })
      );
      history.push(`/admin/banners`);
    } catch (error) {
      dispatch(
        localeActions.addTooltip({
          type: "error",
          message: "Erro ao tentar atualizar banner: " + JSON.stringify(error),
        })
      );
    }
  };

  useEffect(() => {
    if (banner) {
      setTitle(banner.title);
      setDescription(banner.description);
      setCampaignCode(banner.campaign_code);
      setLink(banner.link);
      setPosition(banner.position);
      setStartAtDate(new Date(banner.start_at));
      setExpiresAtDate(new Date(banner.expires_at));
      setEnabled(banner.enabled);
      setImage(banner.image);
    }
  }, [banner]);

  if (!banner || isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <h1>Não foi possivel encontrar o banner: {JSON.stringify(error)}</h1>
    );
  }

  return (
    <Styled.Container>
      <Styled.Content>
        <Styled.Title>Editar Banner</Styled.Title>
        <Form key={banner ? banner.id : "form"} submit={onFormSubmit}>
          <TextInput
            label="Título"
            name="title"
            defaultValue={title}
            placeholder="Digite o título do banner"
          />

          <TextInput
            label="Descriçao"
            name="description"
            defaultValue={description}
            placeholder="Digite a descrição do banner"
          />

          <TextInput
            label="Código da Campanha"
            name="campaign_code"
            placeholder="Digite o código da campanha"
            defaultValue={campaignCode}
            required
          />

          <TextInput
            label="Link"
            name="link"
            defaultValue={link}
            placeholder="Digite o link do banner"
          />

          <Select
            label="Posição do Banner"
            name="position"
            options={bannerPositions}
            placeholder={position}
            defaultValue={position}
            required
          />

          <TextInput
            label="Data de Início"
            name="start_at"
            placeholder="dd/mm/aaaa hh:mm"
            mask="99/99/9999 99:99"
            defaultValue={moment(startAtDate).format(simpleDateFormat)}
          />

          <TextInput
            label="Data de Expiração"
            name="expires_at"
            placeholder="dd/mm/aaaa"
            mask="99/99/9999 99:99"
            defaultValue={moment(expiresAtDate).format(simpleDateFormat)}
          />

          <CheckBox label="Ativo" name="enabled" defaultValue={!!enabled}>
            Ativo
          </CheckBox>

          <img src={image} alt={image} width={200} />

          <Styled.FormRow>
            <FilePond
              files={file}
              allowMultiple={false}
              onupdatefiles={setFile}
              storeAsFile={true}
              maxFileSize="5MB"
              labelIdle={`
        <div class="content-filepond">
            <svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 16 16" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2V3zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0z"></path></svg>
            <p>Clique ou arraste e solte suas <br/> imagens aqui.</p>
        </div>`}
              credits={false}
            />
          </Styled.FormRow>

          <Styled.LocalizationContainer></Styled.LocalizationContainer>
          <Styled.ButtonContainer>
            <ButtonPrimary type="submit" color="orange">
              Salvar
            </ButtonPrimary>
          </Styled.ButtonContainer>
        </Form>
      </Styled.Content>
    </Styled.Container>
  );
};

export default AdminBannersEdit;
